import type { Ref } from "vue";
import { useDirtyTracking } from "./useDirtyTracking";

export interface ListingArrayModelApi<TItem> {
    model: Ref<TItem[]>;
    initial: Ref<TItem[] | undefined>;
    isDirty: Ref<boolean>;

    setAll: (items: TItem[]) => void;
    add: (item: TItem) => void;
    updateAt: (index: number, item: TItem) => void;
    removeAt: (index: number) => void;
    reorder: (fromIndex: number, toIndex: number) => void;
    markSaved: () => void;
    resetAll: () => void;
}

export interface ListingArrayModelDiff<TItem> {
    added: TItem[];
    updated: (TItem & { index: number })[];
    removed: TItem[];
}

export function useListingArrayModel<TItem>(): ListingArrayModelApi<TItem> {
    const { model, initial, isDirty, markSaved, resetAll } = useDirtyTracking<TItem[]>([]);

    function setAll(items: TItem[]) {
        model.value = items || [] as TItem[];
    }

    function add(item: TItem) {
        const copy = [...model.value] as TItem[];
        copy.push(item);
        model.value = copy;
    }

    function updateAt(index: number, item: TItem) {
        const copy = [...model.value.map((prev, i) => i === index ? item : prev)] as TItem[];
        model.value = copy;
    }

    function removeAt(index: number) {
        const copy = [...model.value];
        if (index >= 0 && index < copy.length) {
            copy.splice(index, 1);
            model.value = copy as TItem[];
        }
    }

    function reorder(fromIndex: number, toIndex: number) {
        const copy = [...model.value];
        if (fromIndex >= 0 && fromIndex < copy.length && toIndex >= 0 && toIndex < copy.length) {
            const [removed] = copy.splice(fromIndex, 1);
            if (!removed) return;
            copy.splice(toIndex, 0, removed);
            model.value = copy as TItem[];
        }
    }

    return { 
        model: model as Ref<TItem[]>, 
        initial: initial as Ref<TItem[] | undefined>, 
        isDirty, 
        setAll, 
        add, 
        updateAt, 
        removeAt, 
        reorder,
        markSaved, 
        resetAll 
    };
}

export default useListingArrayModel;
